import { Button, DefinitionItem, DefinitionList, View } from "components/Shared";
import { translate } from "lib";
import _ from "lodash";
import moment from "moment";
import React, { memo } from "react";
import { useHistory } from "react-router-dom";
import StatedBase, { baseIds } from "./StatedBase";

/**
 * WHAT YARDI SAYS ABOUT ONE AMENDMENT — read only, with an Edit button at the
 * bottom for the fields a human may correct (AmendmentEdit).
 *
 * The Details tab of `AmendmentShow`. It is deliberately the SAME SHAPE as the
 * lease's own Details tab (Sim, 26 Aug: "should only show details just like the
 * LeaseAgreement Detail, then at the bottom with Edit button"): a definition
 * list, then the plain shared `<Button>` below it. A detail page states the
 * record; correcting it is a separate screen you choose to go to, not a form you
 * land in.
 *
 * The record is fetched ONCE by AmendmentShow and handed down — this component
 * neither fetches nor writes.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS AND IS NOT CORRECTABLE
 * ---------------------------------------------------------------------------
 * The first four rows below are shown but can never be edited:
 * `amendment_id` is half the dedup key the next /lease_data payload matches on,
 * `amendment_type` decides what the amendment DOES (and already picked the
 * template and workflow), `base_amendment_id` is the tree link the unit fold
 * walks, and cancellation is an amendment ARRIVING rather than a column write.
 * `V2::AmendmentsController#safe_params` is the authority, and AmendmentEdit
 * renders those four locked so it is obvious why their inputs are missing.
 *
 * Everything after them is correctable. A later /lease_data payload can still
 * overwrite every one — this is a correction, not a lock.
 *
 * ASSETS ARE NOT HERE. They belong to this amendment (`assets.amendment_id`,
 * Sim 26 Aug) but only /lease_data writes them, so they get a read-only tab of
 * their own: `AmendmentAssetsTab`.
 */

interface Props {
	amendment: tcpinpoint.Amendment;
	leaseAgreementId: string;
	name?: string;
	icon?: string;
	/** See the lease's own DetailsTab — same flag, same reason. */
	hideEdit?: boolean;
}

/**
 * EVERY ROW IS ALWAYS SHOWN — a blank one reads "Unknown" rather than vanishing
 * (Sim, 27 Aug 2026). Move Out Date, Contraction Date and LRTF Notes were simply
 * absent from this list on an amendment that had none, which reads as "an
 * amendment has no such field" instead of "Yardi has not sent one", and left the
 * list a different length per record so two amendments could not be compared down
 * the page.
 *
 * It has to be a NODE, not a string: `DefinitionItem` renders nothing when
 * `value` is falsy — and, separately, treats the literal string `"unknown"` as
 * absent — so the muted span is what keeps the row on screen. Same helper, same
 * wording, as the lease's own Details tab.
 *
 * A genuine ZERO is still a value: a contracted area or a term of 0 is something
 * Yardi said, so numbers are stringified rather than passed through the falsy
 * check.
 */
const unknown = <span className="muted">{translate("unknown")}</span>;

const stated = (value?: string | number | null) =>
	_.isNil(value) || value === "" ? unknown : _.isNumber(value) ? String(value) : value;

// The fitout term IS the fitout period, so it is read in days and says so. The
// unit rides with the value rather than the label, which already carries the
// second name for the field.
const statedDays = (value?: string | number | null) =>
	_.isNil(value) || value === "" ? unknown : `${value} days`;

// `D MMM YYYY`, matching TenanciesTab in this folder — the dates on a lease are
// read, not sorted, and the month name removes the day/month ambiguity the
// numeric form has. An unparseable value is shown as it arrived rather than as
// "Invalid date"; a missing one reads "Unknown" like every other row.
const statedDate = (value?: string | null) => {
	if (!value) return unknown;
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format("D MMM YYYY") : value;
};

// The types whose Details always carry the AmendmentIDs row.
const TRUNK_TYPES = ["original lease", "seed original lease"];

const AmendmentDetailsTab = ({ amendment, leaseAgreementId, hideEdit }: Props) => {
	const history = useHistory();

	return (
		// NO `title` (Sim, 26 Aug). `View` renders one as an h2 in a card-header,
		// and "Amendment Details" under a tab already labelled Details, on a page
		// already headed "Original Lease #10728826", was the third name for the same
		// thing. Bare `<View>` skips the header block entirely.
		<View>
			{/* NO CANCELLED ALERT. The `Cancelled` row below states the fact, and the
			    Amendments list already carries a red badge for it — a paragraph
			    explaining what cancellation means is the explanatory prose Sim asked
			    to keep out of the UI. What it means lives in Amendment#cancel!. */}
			<DefinitionList>
				{/* LOCKED FIRST, in Sim's order (25 Aug): what the amendment IS, then
				    whether it still stands, then the two ids. The same order AmendmentEdit
				    renders them in, so the two screens read as one. */}
				<DefinitionItem title="amendment_type" value={stated(amendment.amendment_type)} />
				<DefinitionItem
					title="cancelled"
					value={(!!amendment.cancelled ? translate("yes") : translate("no")) as string}
				/>
				<DefinitionItem title="amendment_id" value={stated(amendment.amendment_id)} />
				<DefinitionItem
					title="base_amendment_id"
					value={!!baseIds(amendment).shown ? <StatedBase amendment={amendment} linked /> : unknown}
				/>
				{/* READ ONLY (Sim, 25 Sep 2026): the AmendmentIDs this node answers to —
				    the CurrentLeases extract's AmendmentID cell. ALWAYS shown on an
				    Original Lease and a Seed Original Lease ("Unknown" when the extract
				    matched none, like every other row here); on any other type only
				    when it has one. Nothing in TCP edits it. */}
				{(TRUNK_TYPES.includes(_.toLower(_.trim(_.toString(amendment.amendment_type)))) ||
					!_.isEmpty(amendment.seed_amendment_ids)) && (
					<DefinitionItem
						title="seed_amendment_ids"
						value={
							_.isEmpty(amendment.seed_amendment_ids)
								? unknown
								: `[${_.join(amendment.seed_amendment_ids, ", ")}]`
						}
					/>
				)}

				{/* CORRECTABLE, in AmendmentEdit's order: status, the amendment's own
				    window, the remaining dates, then the term / fitout / area fields. */}
				<DefinitionItem title="amendment_status" value={stated(amendment.amendment_status)} />
				<DefinitionItem title="amendment_start_date" value={statedDate(amendment.amendment_start_date)} />
				<DefinitionItem title="amendment_end_date" value={statedDate(amendment.amendment_end_date)} />
				<DefinitionItem title="move_in_date" value={statedDate(amendment.move_in_date)} />
				<DefinitionItem title="actual_move_in_date" value={statedDate(amendment.actual_move_in_date)} />
				<DefinitionItem title="move_out_date" value={statedDate(amendment.move_out_date)} />
				<DefinitionItem title="contraction_date" value={statedDate(amendment.contraction_date)} />
				{/* "Opening Date (Expected Open for Trade Date)" (Sim, 28 Oct 2026) —
				    the second name is what the scheduling wizard calls this date, the
				    same pattern as "Fit Out Term (Fitout Period)". Its own key: `opening_date`
				    ("Opening Date") is still the edit form's label and the pending
				    list's sort label, and `opening_at` is the lease form's. */}
				<DefinitionItem title="expected_open_for_trade_date" value={statedDate(amendment.opening_at)} />
				<DefinitionItem title="actual_opening_date" value={statedDate(amendment.actual_opening_at)} />
				{/* The term LENGTH in months. NOT the window above — Yardi states both
				    and they can disagree; the window is what the unit fold reads. */}
				<DefinitionItem title="term" value={stated(amendment.term)} />
				<DefinitionItem title="fitout_term" value={statedDays(amendment.fitout_term)} />
				<DefinitionItem title="fitout_type" value={stated(amendment.fitout_type)} />
				<DefinitionItem title="contracted_area" value={stated(amendment.contracted_area)} />
				<DefinitionItem title="unit_of_measure" value={stated(amendment.unit_of_measure)} />
				<DefinitionItem title="unit_status" value={stated(amendment.unit_status)} />
				<DefinitionItem title="sales_category" value={stated(amendment.sales_category)} />
				<DefinitionItem
					title="rdd_activity_description"
					value={stated(amendment.rdd_activity_description)}
				/>
				<DefinitionItem title="lrtf_notes" value={stated(amendment.lrtf_notes)} />
			</DefinitionList>

			{/* The shared <Button>, plain and pencil-less, BELOW the list — identical
			    to the lease's Details tab. The pencil `Link` styling belongs to LIST
			    ROWS (the Tenancies/Tenants/amendment rows); a detail page uses the
			    button. */}
			{!hideEdit && (
				<Button onClick={() => history.push(`/lease-agreements/${leaseAgreementId}/amendments/${amendment.id}/edit`)}>
					{translate("edit")}
				</Button>
			)}
		</View>
	);
};

export default memo(AmendmentDetailsTab);
